import {
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    Typography
} from "@mui/material";
import React,
{
    Dispatch,
    Fragment,
    SetStateAction,
    useMemo
} from "react";

import { useAssessments } from "../dashboard/contexts/AssessmentsContextProvider";
import { useLocations } from "../dashboard/contexts/LocationContextProvider";

type SelectLocationsForScheduleFormProps = {
    existingAssociatedLocationIds?: Array<string>;
    selectedLocationIds: Array<string>;
    setSelectedLocationIds: Dispatch<SetStateAction<Array<string>>>;
};

export const SelectLocationsForScheduleForm = (props: SelectLocationsForScheduleFormProps) => {
    const { existingAssociatedLocationIds = [], selectedLocationIds, setSelectedLocationIds } = props;
    const { locations } = useLocations();
    const { assessmentSchedules, assessmentScheduleIdToResourceIdsMap } = useAssessments();

    const locationIdTAssessmentScheduleIdsMap = useMemo<Map<string, Array<string>>>(() => {
        return Array.from(assessmentScheduleIdToResourceIdsMap?.entries() ?? []).reduce((locationIdTAssessmentSchedulesMap, [assessmentScheduleId, resourceIds]) => {
            resourceIds.forEach(resourceId => {
                const locationId = resourceId;
                const assessmentScheduleIds = locationIdTAssessmentSchedulesMap.get(locationId) ?? [];
                locationIdTAssessmentSchedulesMap.set(locationId, [...assessmentScheduleIds, assessmentScheduleId]);
            });
            return locationIdTAssessmentSchedulesMap;
        }, new Map<string, Array<string>>());
    }, [assessmentScheduleIdToResourceIdsMap]);

    const selectableLocations = useMemo(() => {
        const locationIdsWithAssessmentSchedule: Array<string> = Array.from(assessmentScheduleIdToResourceIdsMap?.values() ?? []).flat();
        return locations.filter(location => existingAssociatedLocationIds.includes(location.id) || !locationIdsWithAssessmentSchedule.includes(location.id));
    }, [assessmentScheduleIdToResourceIdsMap, locations]);

    return (
        <Box display="flex" flexDirection="column" gap={0.5}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={selectedLocationIds.length > 0 && selectedLocationIds.length === selectableLocations.length}
                        onChange={(e, checked) => {
                            setSelectedLocationIds(checked ? selectableLocations.map(location => location.id) : []);
                        }}
                        disabled={selectableLocations.length === 0}
                    />
                }
                label={<Typography>Select All</Typography>}
                sx={{ gridColumn: "1 / -1" }}
            />
            {locations.map((location, index) => {
                const assignedScheduleId = locationIdTAssessmentScheduleIdsMap.get(location.id)?.[0];
                const assignedSchedule = assignedScheduleId ? assessmentSchedules?.find(schedule => schedule.id === assignedScheduleId) : undefined;
                return (
                    <Fragment key={location.id}>
                        <Box display="flex" alignItems="center">
                            <Checkbox
                                checked={selectedLocationIds.includes(location.id)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedLocationIds(prev => [...prev, location.id]);
                                    } else {
                                        setSelectedLocationIds(prev => prev.filter(id => id !== location.id));
                                    }
                                }}
                                disabled={!selectableLocations.includes(location)}
                            />
                            <Box display="flex" flexDirection="column">
                                <Typography>{location.name}</Typography>
                                {assignedSchedule && (
                                    <Box display="flex" alignItems="center">
                                        <Typography variant="body2">(Assigned to:&nbsp;</Typography>
                                        <Typography variant="body2" fontWeight={500}>{assignedSchedule.name}</Typography>
                                        <Typography variant="body2">)</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        {index < locations.length - 1 && <Divider variant="middle" />}
                    </Fragment>
                );
            })}
        </Box>
    );
};